/**
 * StickerGenerator.js - Generador de hojas de stickers para objetos Premium
 *
 * Usa una plantilla de hoja completa (sticker_sheet) tipo A4 con hasta 16 QRs posicionados.
 * El usuario sube su diseño completo y posiciona los QRs donde van los stickers.
 *
 * Genera:
 * - PNG con la hoja de stickers (plantilla + QRs superpuestos)
 * - Descarga directa como PNG (el usuario maneja el corte en Silhouette)
 */

import React, { useState, useCallback, useEffect } from 'react';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';
import { API_URL } from '../../../services/api/config';
import { getAuthHeaders } from '../../../services/api/utils/fetchWrapper';
import { logger } from '../../../utils/logger';

// Dimensiones A4 a 300dpi
const A4_DIMENSIONS = {
  width: 2480,   // 210mm at 300dpi
  height: 3508   // 297mm at 300dpi
};

function StickerGenerator({ onBack, addToHistory }) {
  const [foliosText, setFoliosText] = useState('');
  const [validatedObjects, setValidatedObjects] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Estado de plantilla
  const [template, setTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState(null);

  // Cargar plantilla sticker_sheet al montar
  useEffect(() => {
    loadStickerTemplate();
  }, []);

  const loadStickerTemplate = async () => {
    setTemplateLoading(true);
    setTemplateError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/plantillas`, { headers });

      if (!response.ok) {
        setTemplateError('Error cargando plantillas');
        return;
      }

      const data = await response.json();
      const allPlantillas = data.data || [];

      // Buscar plantilla tipo sticker_sheet
      const stickerSheet = allPlantillas.find(p => p.tipo === 'sticker_sheet');

      if (!stickerSheet) {
        setTemplateError('No existe plantilla de hoja de stickers');
        return;
      }

      if (!stickerSheet.imagenUrl) {
        setTemplateError('La plantilla no tiene imagen de fondo');
        return;
      }

      const qrFields = stickerSheet.campos?.filter(c => c.tipo === 'qrConsulta') || [];
      if (qrFields.length === 0) {
        setTemplateError('La plantilla no tiene posiciones de QR configuradas');
        return;
      }

      setTemplate(stickerSheet);
      logger.debug('Stickers - Plantilla cargada:', stickerSheet.nombre, `(${qrFields.length} QRs)`);
    } catch (err) {
      logger.error('Stickers - Error:', err);
      setTemplateError('Error de conexión');
    } finally {
      setTemplateLoading(false);
    }
  };

  // Parsear folios del textarea
  const parseFolios = useCallback(() => {
    return foliosText
      .split('\n')
      .map(f => f.trim().toUpperCase())
      .filter(f => f.length > 0);
  }, [foliosText]);

  // Validar un folio contra el backend
  const validateFolio = async (folio) => {
    const url = `${API_URL}/api/credenciales/verificar-folio/${encodeURIComponent(folio)}`;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(url, { headers });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return { error: errorData.error || `Folio ${folio} no encontrado` };
      }

      const data = await response.json();

      if (!data.existe) {
        return { error: `Folio ${folio} no encontrado` };
      }

      if (data.tipo === 'mascota') {
        return { error: `Folio ${folio} es mascota, stickers solo para objetos` };
      }

      if (data.plan !== 'premium') {
        return { error: `Folio ${folio} no es Premium (es ${data.plan || 'sin plan'})` };
      }

      return {
        success: true,
        object: {
          id: data.id,
          folio: data.folio,
          nombre: data.nombre || data.nombreObjeto || 'Sin nombre',
          tipo: data.tipo,
          plan: data.plan
        }
      };
    } catch (err) {
      logger.error('Stickers - Error:', err);
      return { error: `Error de conexión para folio ${folio}` };
    }
  };

  // Validar todos los folios
  const handleValidate = async () => {
    const folios = parseFolios();
    if (folios.length === 0) return;

    setLoading(true);
    setErrors([]);
    setValidatedObjects([]);

    const newErrors = [];
    const newObjects = [];

    for (const folio of folios) {
      const result = await validateFolio(folio);
      if (result.error) {
        newErrors.push(result.error);
      } else {
        if (!newObjects.find(o => o.id === result.object.id)) {
          newObjects.push(result.object);
        }
      }
    }

    setErrors(newErrors);
    setValidatedObjects(newObjects);
    setLoading(false);
  };

  // Generar QR como data URL
  const generateQR = async (objetoId, size = 200) => {
    const url = `https://app.lemmontags.com/consultar?registro=${objetoId}`;
    return await QRCode.toDataURL(url, {
      width: size,
      margin: 0,
      errorCorrectionLevel: 'M'
    });
  };

  // Cargar imagen
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Error cargando imagen'));
      img.src = src;
    });
  };

  // Generar hoja de stickers para un objeto
  const generateSheet = async (object) => {
    // Crear canvas A4
    const canvas = document.createElement('canvas');
    canvas.width = A4_DIMENSIONS.width;
    canvas.height = A4_DIMENSIONS.height;
    const ctx = canvas.getContext('2d');

    // Fondo blanco
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Dibujar imagen de plantilla
    try {
      const templateImg = await loadImage(template.imagenUrl);
      ctx.drawImage(templateImg, 0, 0, canvas.width, canvas.height);
    } catch (err) {
      logger.error('Error cargando imagen de plantilla:', err);
      throw new Error('No se pudo cargar la imagen de la plantilla');
    }

    // Generar QR para este objeto
    const qrDataUrl = await generateQR(object.id, 300);
    const qrImg = await loadImage(qrDataUrl);

    // Obtener posiciones de QR de la plantilla
    const qrFields = template.campos?.filter(c => c.tipo === 'qrConsulta') || [];

    // Calcular escala (la plantilla puede tener dimensiones diferentes)
    const templateWidth = template.dimensiones?.ancho || A4_DIMENSIONS.width;
    const templateHeight = template.dimensiones?.alto || A4_DIMENSIONS.height;
    const scaleX = canvas.width / templateWidth;
    const scaleY = canvas.height / templateHeight;

    // Dibujar QR en cada posición configurada
    for (const field of qrFields) {
      const x = field.x * scaleX;
      const y = field.y * scaleY;
      const w = (field.width || 100) * scaleX;
      const h = (field.height || 100) * scaleY;

      ctx.drawImage(qrImg, x, y, w, h);

      // Agregar folio debajo del QR (pequeño, centrado)
      const folioNum = String(object.folio).replace(/\D/g, '').replace(/^0+/, '') || '0';
      ctx.fillStyle = '#000000';
      ctx.font = '24px Arial, sans-serif'; // ~8pt a 300dpi
      ctx.textAlign = 'center';
      ctx.fillText(`F-${folioNum}`, x + w / 2, y + h + 25);
    }

    // Convertir a blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    });
  };

  // Verificar si la plantilla está lista
  const templateReady = template && template.imagenUrl && template.campos?.length > 0;
  const qrCount = template?.campos?.filter(c => c.tipo === 'qrConsulta').length || 0;

  // Generar y descargar stickers
  const handleGenerate = async () => {
    if (validatedObjects.length === 0 || !templateReady) return;

    setGenerating(true);

    try {
      for (const obj of validatedObjects) {
        const blob = await generateSheet(obj);
        saveAs(blob, `stickers_${obj.folio}.png`);
      }

      addToHistory?.(`Stickers generados: ${validatedObjects.length} hojas`);
    } catch (err) {
      logger.error('Error generando stickers:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="sticker-generator">
      {/* Header */}
      <div
        className="d-flex align-items-center gap-3"
        style={{ marginBottom: 'var(--space-5)' }}
      >
        <button
          className="btn btn-sm"
          onClick={onBack}
          style={{
            backgroundColor: 'var(--gray-100)',
            color: 'var(--gray-700)'
          }}
        >
          <i className="bi bi-arrow-left"></i>
        </button>
        <h5
          style={{
            margin: 0,
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--gray-900)'
          }}
        >
          <i className="bi bi-stickies me-2" style={{ color: 'var(--feature-primary)' }}></i>
          Generar Stickers
        </h5>
      </div>

      <div className="row">
        <div className="col-12 col-lg-8">
          {/* Estado de plantilla */}
          {templateLoading && (
            <div className="alert alert-info mb-4" style={{ borderRadius: 'var(--radius-lg)' }}>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Cargando plantilla...
            </div>
          )}

          {templateError && (
            <div className="alert alert-warning mb-4" style={{ borderRadius: 'var(--radius-lg)' }}>
              <strong><i className="bi bi-exclamation-triangle me-2"></i>{templateError}</strong>
              <p className="mb-0 mt-2">
                Ve a "Mis Plantillas" y crea una plantilla tipo "Hoja de Stickers" con tu diseño A4 y las posiciones de los QRs.
              </p>
            </div>
          )}

          {templateReady && (
            <div className="alert alert-success mb-4" style={{ borderRadius: 'var(--radius-lg)' }}>
              <strong><i className="bi bi-check-circle me-2"></i>Plantilla lista</strong>
              <span className="ms-2">"{template.nombre}" con {qrCount} posiciones de QR</span>
            </div>
          )}

          {/* Input de folios */}
          <div
            className="card mb-4"
            style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)'
            }}
          >
            <div
              className="card-header"
              style={{
                backgroundColor: 'var(--gray-50)',
                borderBottom: '1px solid var(--gray-200)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              <i className="bi bi-list-ol me-2"></i>
              Números de folio
            </div>
            <div className="card-body">
              <textarea
                className="form-control"
                rows={6}
                value={foliosText}
                onChange={e => {
                  setFoliosText(e.target.value);
                  setValidatedObjects([]);
                  setErrors([]);
                }}
                placeholder="Ingresa los números de folio, uno por línea...

Ejemplo:
P000047
P000048"
                style={{
                  borderColor: 'var(--gray-300)',
                  fontFamily: 'monospace',
                  fontSize: 'var(--font-size-sm)'
                }}
                disabled={!templateReady}
              />

              <div className="d-flex justify-content-between align-items-center mt-3">
                <small style={{ color: 'var(--gray-500)' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Solo objetos Premium (no mascotas)
                </small>
                <button
                  className="btn btn-sm"
                  onClick={handleValidate}
                  disabled={loading || parseFolios().length === 0 || !templateReady}
                  style={{
                    backgroundColor: 'var(--gray-700)',
                    color: 'var(--white)'
                  }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Validando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check2-circle me-1"></i>
                      Validar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Errores */}
          {errors.length > 0 && (
            <div className="alert alert-warning mb-4" style={{ borderRadius: 'var(--radius-lg)' }}>
              <strong><i className="bi bi-exclamation-triangle me-2"></i>Errores:</strong>
              <ul className="mb-0 mt-2">
                {errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Objetos validados */}
          {validatedObjects.length > 0 && (
            <div
              className="card mb-4"
              style={{
                border: '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-lg)'
              }}
            >
              <div
                className="card-header"
                style={{
                  backgroundColor: 'var(--feature-accent)',
                  borderBottom: '1px solid var(--gray-200)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}
              >
                <i className="bi bi-check-circle me-2" style={{ color: 'var(--feature-primary)' }}></i>
                Objetos válidos ({validatedObjects.length})
              </div>
              <ul className="list-group list-group-flush">
                {validatedObjects.map((obj) => (
                  <li key={obj.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>
                      <strong style={{ color: 'var(--feature-primary)' }}>{obj.folio}</strong>
                      <span className="ms-2" style={{ color: 'var(--gray-600)' }}>{obj.nombre}</span>
                    </span>
                    <span className="badge" style={{ backgroundColor: 'var(--feature-primary)', color: 'white' }}>
                      {obj.tipo}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Botón generar */}
          <div
            className="card"
            style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--gray-50)'
            }}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
                    {validatedObjects.length} objeto{validatedObjects.length !== 1 ? 's' : ''}
                  </div>
                  <small style={{ color: 'var(--gray-500)' }}>
                    {validatedObjects.length} hoja{validatedObjects.length !== 1 ? 's' : ''} PNG
                  </small>
                </div>

                <button
                  className="btn btn-lg"
                  onClick={handleGenerate}
                  disabled={generating || validatedObjects.length === 0 || !templateReady}
                  style={{
                    backgroundColor: 'var(--feature-primary)',
                    color: 'var(--white)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}
                >
                  {generating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Generando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-2"></i>
                      Descargar PNGs
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-12 col-lg-4 mt-4 mt-lg-0">
          {/* Instrucciones */}
          <div
            className="card"
            style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)'
            }}
          >
            <div
              className="card-header"
              style={{
                backgroundColor: 'var(--gray-50)',
                borderBottom: '1px solid var(--gray-200)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              <i className="bi bi-info-circle me-2"></i>
              Instrucciones
            </div>
            <div className="card-body" style={{ fontSize: 'var(--font-size-sm)' }}>
              <ol style={{ paddingLeft: 'var(--space-4)', margin: 0 }}>
                <li className="mb-2">Crea plantilla "Hoja de Stickers"</li>
                <li className="mb-2">Sube tu diseño A4 (con stickers sin QR)</li>
                <li className="mb-2">Posiciona hasta 16 QRs donde van</li>
                <li className="mb-2">Ingresa folios de objetos Premium</li>
                <li className="mb-2">Descarga PNG por cada objeto</li>
                <li>Imprime y corta en Silhouette</li>
              </ol>

              <hr style={{ borderColor: 'var(--gray-200)' }} />

              <div style={{ color: 'var(--gray-600)' }}>
                <strong>Formato:</strong> A4 (210×297mm)
                <br />
                <strong>Resolución:</strong> 300 DPI
                <br />
                <strong>Salida:</strong> PNG por objeto
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StickerGenerator;
